<template>
  <v-dialog
    v-model="dialog"
    transition="dialog-bottom-transition"
    persistent
    max-width="1000px"
    v-if="isDataLoadedFromServer"
  >
    <div class="bg-white poppins">
      <div class="modal-header align-center">
        <h3 class="mb-0 font-weight-bolder">Edit Courier Account</h3>
        <div
          class="btn btn-sm btn-icon btn-active-light-info"
          @click="toggleModal"
        >
          <span class="svg-icon">
            <v-icon size="22">mdi-close</v-icon>
          </span>
        </div>
      </div>
      <div class="modal-body d-flex flex-column scroll-y mx-5 mx-xl-15 py-7">
        <!--begin::Body-->
        <div class="row">
          <div class="col-12 col-sm-6 col-md-4 py-0">
            <v-text-field
              v-model="formData.title"
              :label="$t('title')"
              clearable
              outlined
              dense
              :error-messages="titleErrors"
              @blur="$v.formData.title.$touch()"
            />
          </div>
          <div class="col-12 col-sm-6 col-md-4 py-0">
            <v-select
              v-model="formData.carrier_id"
              :label="$t('carrier')"
              item-text="name"
              item-value="id"
              :items="serverData.carriers"
              clearable
              outlined
              dense
              required
              :error-messages="carrier_idErrors"
              @blur="$v.formData.carrier_id.$touch()"
            />
          </div>
          <div class="col-12 col-sm-6 col-md-4 py-0">
            <v-select
              v-model="formData.template_type"
              :label="$t('template_type')"
              item-text="text"
              item-value="index"
              :items="serverData.template_types"
              clearable
              outlined
              dense
              required
              :error-messages="template_typeErrors"
              @blur="$v.formData.template_type.$touch()"
            />
          </div>
          <div class="col-12 col-sm-6 py-0">
            <v-select
              v-model="formData.available_pickup_tracking"
              :label="$t('available_pickup_tracking')"
              item-text="text"
              item-value="index"
              :items="[
                { text: 'Yes', index: 1 },
                { text: 'No', index: 0 },
              ]"
              clearable
              outlined
              dense
            />
          </div>
          <div class="col-12 col-sm-6 py-0">
            <v-text-field
              v-model="formData.email"
              :label="$t('email')"
              clearable
              outlined
              dense
              :error-messages="emailErrors"
              @blur="$v.formData.email.$touch()"
            />
          </div>

          <div class="col-12 py-0">
            <v-text-field
              v-model="formData.url"
              :label="$t('url')"
              clearable
              outlined
              dense
            />
          </div>
          <div class="col-12 col-sm-6 py-0">
            <v-select
              v-model="formData.rvp_tracking_key"
              :label="$t('rvp_tracking_key')"
              item-text="text"
              item-value="index"
              :items="serverData.rvp_tracking_keys"
              clearable
              outlined
              dense
            />
          </div>
          <div class="col-12 col-sm-6 py-0">
            <v-select
              v-model="formData.order_tracking_key"
              :label="$t('order_tracking_key')"
              item-text="text"
              item-value="index"
              :items="serverData.order_tracking_keys"
              clearable
              outlined
              dense
            />
          </div>
          <div class="col-12 col-sm-6 py-0">
            <v-text-field
              v-model="formData.pickup_close_on_status"
              :label="$t('pickup_close_on_status')"
              clearable
              outlined
              dense
            />
          </div>

          <div class="col-12 col-sm-6 py-0">
            <v-text-field
              v-model="formData.delivery_no_prefix"
              label="Delivery No Prefix"
              clearable
              outlined
              dense
            />
          </div>

          <div class="col-12 col-sm-6 py-0">
            <v-autocomplete
              v-model="formData.temperature_option"
              label="Service type"
              item-name="text"
              item-value="index"
              :items="serverData.temperature_options"
              clearable
              outlined
              dense
            />
          </div>

          <div class="col-12 text-center pb-4">
            <h3>Credentials</h3>
            <v-jsoneditor v-model="formData.credentials" :options="options" />
          </div>

          <template v-if="formData.template_type === 1">
            <div class="text-center col-12">
              <h3>Label template</h3>
              <ckeditor
                v-model="formData.label_template"
                :config="editorConfig"
              />
              <span class="text-danger text-start d-block px-3">{{
                label_templateErrors[0]
              }}</span>
            </div>

            <div class="col-12 col-sm-6">
              <v-text-field
                v-model="formData.label_width"
                :label="$t('label_width')"
                clearable
                type="Number"
                outlined
                dense
                @change="() => validateMinValue('label_width', 0)"
                :error-messages="label_widthErrors"
                @blur="$v.formData.label_width.$touch()"
              />
            </div>

            <div class="col-12 col-sm-6">
              <v-text-field
                v-model="formData.label_height"
                :label="$t('label_height')"
                clearable
                type="Number"
                outlined
                dense
                @change="() => validateMinValue('label_height', 0)"
                :error-messages="label_heightErrors"
                @blur="$v.formData.label_height.$touch()"
              />
            </div>
          </template>
        </div>
        <!--end::Body-->
        <div class="my-4 d-flex flex-grow-1 align-end justify-center">
          <button
            type="reset"
            class="btn btn-light mr-3 px-5 py-3 ls1"
            @click="resetEditForm"
          >
            Reset
          </button>
          <button
            type="submit"
            class="btn btn-info px-5 py-3 ls1"
            @click="submitEditForm"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";
import validationMixin from "@/own/mixins/validationMixin.js";
import { email, required } from "vuelidate/lib/validators";
import VJsoneditor from "v-jsoneditor/src/index";
import fieldValueValidation from "@/own/mixins/fieldValueValidation.js";

export default {
  name: "EditItem",
  mixins: [validationMixin, fieldValueValidation],
  components: { VJsoneditor },
  validations() {
    return this.formData.template_type === 1
      ? {
          formData: {
            carrier_id: { required },
            label_width: {
              required,
            },
            label_height: {
              required,
            },
            label_template: {
              required,
            },
            template_type: { required },
            title: { required },
            email: { email },
          },
        }
      : {
          formData: {
            carrier_id: { required },
            template_type: { required },
            title: { required },
            email: { email },
          },
        };
  },
  props: ["refresher", "pageLoader"],
  data: () => ({
    dialog: false,
    serverData: null,
    formData: {
      carrier_id: null,
      url: null,
      template_type: null,
      label_template: null,
      label_width: null,
      label_height: null,
      title: null,
      credentials: {},
      email: null,
      available_pickup_tracking: 0,
      rvp_tracking_key: null,
      order_tracking_key: null,
      pickup_close_on_status: null,
      temperature_option: null,
    },
    options: { mode: "code", navigationBar: true, mainMenuBar: false },
    editorConfig: {
      versionCheck: false,
      toolbar: [
        {
          name: "document",
          items: ["Source", "-", "Preview", "Print", "-"],
        },
        {
          name: "clipboard",
          items: [
            "Cut",
            "Copy",
            "Paste",
            "PasteText",
            "PasteFromWord",
            "-",
            "Undo",
            "Redo",
          ],
        },
        {
          name: "editing",
          items: ["Find", "Replace", "-", "SelectAll", "-", "Scayt"],
        },
        {
          name: "forms",
          items: ["Button", "ImageButton", "HiddenField"],
        },
        {
          name: "basicstyles",
          items: [
            "Bold",
            "Italic",
            "Underline",
            "Strike",
            "Subscript",
            "Superscript",
            "-",
            "RemoveFormat",
          ],
        },
        {
          name: "paragraph",
          items: [
            "NumberedList",
            "BulletedList",
            "-",
            "Outdent",
            "Indent",
            "-",
            "Blockquote",
            "CreateDiv",
            "-",
            "JustifyLeft",
            "JustifyCenter",
            "JustifyRight",
            "JustifyBlock",
            "-",
            "BidiLtr",
            "BidiRtl",
          ],
        },
        {
          name: "insert",
          items: [
            "Image",
            "Table",
            "HorizontalRule",
            "Smiley",
            "SpecialChar",
            "PageBreak",
          ],
        },
        { name: "styles", items: ["Styles", "Format", "Font", "FontSize"] },
        { name: "colors", items: ["TextColor", "BGColor"] },
        { name: "tools", items: ["Maximize", "ShowBlocks"] },
        {
          name: "about",
          items: ["About", "DocProps", "CreatePlaceholder"],
        },
      ],
      fullPage: false,
      allowedContent: true,
      height: 300,
      extraPlugins: [
        "docprops",
        "placeholder",
        "about",
        "a11yhelp",
        "basicstyles",
        "bidi",
        "blockquote",
        "clipboard",
        "colorbutton",
        "colordialog",
        "contextmenu",
        "copyformatting",
        "dialogadvtab",
        "div",
        "elementspath",
        "enterkey",
        "entities",
        "exportpdf",
        "filebrowser",
        "find",
        "floatingspace",
        "font",
        "format",
        "forms",
        "horizontalrule",
        "htmlwriter",
        "iframe",
        "image",
        "indentblock",
        "indentlist",
        "justify",
        "language",
        "link",
        "list",
        "liststyle",
        "magicline",
        "maximize",
        "newpage",
        "pagebreak",
        "pastefromword",
        "pastetext",
        "preview",
        "print",
        "removeformat",
        "resize",
        "save",
        "scayt",
        "selectall",
        "showblocks",
        "showborders",
        "smiley",
        "sourcearea",
        "specialchar",
        "stylescombo",
        "tab",
        "table",
        "tabletools",
        "templates",
        "toolbar",
        "undo",
        "wysiwygarea",
        "tableresize",
        "image2,uploadimage",
        "colordialog,tableresize",
      ],
      resize_enable: true,
    },
  }),
  methods: {
    loadDataFromServer() {
      this.pageLoader(true);
      let data = { account_id: this.$store.getters.getItemForAction.id };
      ApiService.post("/shipping/courierAccounts/edit", data)
        .then((response) => {
          this.serverData = response.data;
          this.formData = { ...this.serverData.carrier };
          this.pageLoader(false);
        })
        .catch(() => {
          this.dialog = false;
          this.pageLoader(false);
        });
    },
    convertToFormData() {
      let data = new FormData();
      Object.entries(this.formData).forEach((item) => {
        if (
          (item[1] && item[0] != "image" && item[0] != "photo") ||
          item[1] == 0
        ) {
          if (Array.isArray(item[1])) {
            item[1].forEach((it) => data.append(`${item[0]}[]`, it));
          } else if (
            typeof item[1] === "object" &&
            item[1] !== null &&
            !Array.isArray(item[1])
          ) {
            data.append(item[0], JSON.stringify(item[1]));
          } else {
            data.append(item[0], item[1]);
          }
        } else if (item[1] && (item[0] == "image" || item[0] == "photo")) {
          // console.log(item[1], item[0], item[1][0]);
          data.append(item[0], item[1][0]);
        }
      });
      return data;
    },

    submitEditForm() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.pageLoader(true);
      let data = this.convertToFormData();
      data.append("account_id", this.$store.getters.getItemForAction.id);
      ApiService.post("/shipping/courierAccounts/update", data)
        .then(() => {
          Swal.fire({
            title: "Updated",
            text: `Courier account  data has been updated`,
            icon: "success",
            showConfirmButton: false,
            timer: 2500,
          });
          this.toggleModal();
          this.refresher();
        })
        .catch(() => {
          this.pageLoader(false);
        });
    },
    resetEditForm() {
      this.$v.$reset();
      this.formData = {
        carrier_id: null,
        url: null,
        template_type: null,
        label_template: null,
        label_width: null,
        label_height: null,
        title: null,
        credentials: {},
        available_pickup_tracking: 0,
        rvp_tracking_key: null,
        order_tracking_key: null,
        pickup_close_on_status: null,
        temperature_option: null,
      };
    },
    toggleModal() {
      if (this.dialog) this.resetEditForm();
      else this.loadDataFromServer();

      this.dialog = !this.dialog;
    },
  },
  computed: {
    isDataLoadedFromServer: function () {
      return !!this.serverData;
    },
    carrier_idErrors() {
      return this.handleFormValidation("carrier_id", this);
    },
    template_typeErrors() {
      return this.handleFormValidation("template_type", this);
    },
    label_widthErrors() {
      return this.handleFormValidation("label_width", this);
    },
    label_heightErrors() {
      return this.handleFormValidation("label_height", this);
    },
    label_templateErrors() {
      return this.handleFormValidation("label_template", this);
    },
    titleErrors() {
      return this.handleFormValidation("title", this);
    },
    emailErrors() {
      return this.handleFormValidation("email", this);
    },
  },
};
</script>
